import { Line } from '@react-three/drei'
import { useEffect, useMemo } from 'react'
import { BufferGeometry, DoubleSide, Float32BufferAttribute } from 'three'
import { rotacaoYAFavorDoVento } from '../../dominio/vento'
import { Rotulo3D } from '../componentes/Rotulo3D'

export interface ContornoDesenhavel {
  id: string
  /** Borda [x a favor do vento, meia-largura], de `contornoNoSolo`. */
  borda: ReadonlyArray<readonly [number, number]>
  cor: string
  opacidade: number
  rotulo?: string
  tracejado?: boolean
}

const nenhumRaio = () => null

/** Corta a borda na frente da pluma, interpolando a meia-largura no corte. */
function cortarNaFrente(borda: ContornoDesenhavel['borda'], frenteM: number): Array<readonly [number, number]> {
  const cortada: Array<readonly [number, number]> = []
  for (let i = 0; i < borda.length; i++) {
    const [x, w] = borda[i]!
    if (x <= frenteM) {
      cortada.push([x, w])
      continue
    }
    const anterior = borda[i - 1]
    if (anterior) {
      const t = (frenteM - anterior[0]) / (x - anterior[0])
      cortada.push([frenteM, anterior[1] + (w - anterior[1]) * t])
    }
    break
  }
  return cortada
}

/** Teto do "caimento" da nuvem — ilustrativo, não um perfil de concentração vertical. */
const ALTURA_PSEUDOVOLUME_MAX_M = 3

function Faixa({ borda, cor, opacidade, ordem }: { borda: ReadonlyArray<readonly [number, number]>; cor: string; opacidade: number; ordem: number }) {
  const geometria = useMemo(() => {
    // faixa monótona em x: três vértices por estação (borda esquerda, crista, borda direita) —
    // um "telhado" baixo em vez de um decalque plano, para a pluma ler como volume, não desenho no chão.
    const posicoes: number[] = []
    const indices: number[] = []
    borda.forEach(([x, w], i) => {
      const altura = Math.min(ALTURA_PSEUDOVOLUME_MAX_M, w * 0.4)
      posicoes.push(-w, 0, x, 0, altura, x, w, 0, x)
      if (i > 0) {
        const a = (i - 1) * 3
        // face esquerda (borda → crista) e face direita (crista → borda)
        indices.push(a, a + 3, a + 4, a, a + 4, a + 1, a + 1, a + 4, a + 5, a + 1, a + 5, a + 2)
      }
    })
    const g = new BufferGeometry()
    g.setAttribute('position', new Float32BufferAttribute(posicoes, 3))
    g.setIndex(indices)
    return g
  }, [borda])
  useEffect(() => () => geometria.dispose(), [geometria])
  return (
    <mesh geometry={geometria} position={[0, 0.03, 0]} raycast={nenhumRaio} renderOrder={ordem}>
      <meshBasicMaterial
        color={cor}
        transparent
        opacity={opacidade}
        depthWrite={false}
        side={DoubleSide}
        polygonOffset
        polygonOffsetFactor={-4 - ordem}
        polygonOffsetUnits={-4 - ordem}
      />
    </mesh>
  )
}

/**
 * Contornos de concentração no solo, no referencial da pluma (fonte na
 * origem, +Z local a favor do vento). Do mais externo ao mais interno, para o
 * contorno de concentração maior pintar por cima.
 */
export function ContornosPluma3D({
  contornos,
  direcaoVentoGraus,
  frenteM = Infinity,
  children,
}: {
  contornos: readonly ContornoDesenhavel[]
  direcaoVentoGraus: number
  /** Até onde a pluma já chegou (animação); além disso não se desenha. */
  frenteM?: number
  /** Desenhos extras no mesmo referencial (área de ação protetora do GRE). */
  children?: React.ReactNode
}) {
  return (
    <group rotation={[0, rotacaoYAFavorDoVento(direcaoVentoGraus), 0]}>
      {contornos.map((contorno, ordem) => {
        const borda = cortarNaFrente(contorno.borda, frenteM)
        if (borda.length < 2) return null
        const contornoFechado: Array<[number, number, number]> = [
          ...borda.map(([x, w]) => [w, 0.08, x] as [number, number, number]),
          ...[...borda].reverse().map(([x, w]) => [-w, 0.08, x] as [number, number, number]),
        ]
        contornoFechado.push(contornoFechado[0]!)
        const ponta = borda[borda.length - 1]!
        return (
          <group key={contorno.id}>
            <Faixa borda={borda} cor={contorno.cor} opacidade={contorno.opacidade} ordem={ordem} />
            <Line points={contornoFechado} color={contorno.cor} lineWidth={2} dashed={contorno.tracejado} dashSize={8} gapSize={6} raycast={nenhumRaio} />
            {contorno.rotulo && frenteM >= ponta[0] && (
              <Rotulo3D posicao={[0, 3, ponta[0]]} texto={contorno.rotulo} variante="zona" fator={Math.max(40, ponta[0] * 0.3)} />
            )}
          </group>
        )
      })}
      {children}
    </group>
  )
}

/**
 * Área de ação protetora do GRE no referencial da pluma: o quadrado a favor
 * do vento com lado igual à distância de proteção, e o círculo de isolamento.
 */
export function AreaAcaoProtetora3D({ isolarM, protegerM, cor = '#60a5fa' }: { isolarM: number; protegerM: number; cor?: string }) {
  const circulo = useMemo(
    () =>
      Array.from({ length: 97 }, (_, i) => {
        const a = (i / 96) * Math.PI * 2
        return [Math.sin(a) * isolarM, 0.1, Math.cos(a) * isolarM] as [number, number, number]
      }),
    [isolarM],
  )
  const meio = protegerM / 2
  const quadrado: Array<[number, number, number]> = [
    [-meio, 0.1, 0],
    [-meio, 0.1, protegerM],
    [meio, 0.1, protegerM],
    [meio, 0.1, 0],
    [-meio, 0.1, 0],
  ]
  return (
    <group>
      <Line points={circulo} color={cor} lineWidth={2} dashed dashSize={6} gapSize={5} raycast={nenhumRaio} />
      {protegerM > 0 && (
        <>
          <Line points={quadrado} color={cor} lineWidth={2} dashed dashSize={12} gapSize={8} raycast={nenhumRaio} />
          <Rotulo3D posicao={[meio, 3, protegerM]} texto="GRE: proteger" subtexto={`${Math.round(protegerM)} m a favor do vento`} variante="neutro" fator={Math.max(40, protegerM * 0.3)} />
        </>
      )}
    </group>
  )
}
